/**
 * One template worksheet fanned out into a sheet per group key, and kept in step with the data on
 * later runs — sheets whose group disappeared are deleted, survivors are re-dated, new groups get a
 * fresh copy of the template.
 */
import { dateTimeNowForExcel } from "../../../utils/format";
import { safeWorksheetName } from "../../workbook";
import { getWorksheetCustomProperties, WORKSHEET_CUSTOM_PROPERTIES } from "../../workbook/customProperties";
import { applyRangeFormats, rangeFormatting } from "../../styles/worksheet";
import { rangeNames } from "./rangeNames";
import { updateReportDate } from "./common";
import { groupDataByKey } from "./groupings";

export interface SheetDataItem {
  key: string;
  label: string;
  data: any[];
  sheetName: string;
}

// Default options per report type; keys drive option definitions (checkboxes derived from boolean keys).

export const dateStampSheet = async (sheet: Excel.Worksheet) => {
  const usedRange = sheet.getUsedRange();
  usedRange.load(["rowIndex", "columnIndex", "columnCount"]);
  await sheet.context.sync();

  const topRightCell = sheet.getRangeByIndexes(
    usedRange.rowIndex,
    usedRange.columnIndex + usedRange.columnCount - 1,
    1,
    1
  );
  topRightCell.load("address");
  await sheet.context.sync();

  applyRangeFormats(sheet, [
    {
      rangeAddress: topRightCell.address,
      value: dateTimeNowForExcel(),
      numberFormat: "mm/dd/yyyy",
      bold: true,
      name: rangeNames.date,
    },
  ] as rangeFormatting[]);

  await sheet.context.sync();
};

const generateDynamicSheetWithTemplate = async (
  context: Excel.RequestContext,
  workbook: Excel.Workbook,
  template: Excel.Worksheet,
  reportType: string,
  key: string,
  dictionary: Record<string, { data: any[]; label: any }>
): Promise<SheetDataItem> => {
  // Copy sheet from template
  const newSheet = template.copy(Excel.WorksheetPositionType.end);
  await context.sync();

  const keys = Object.keys(dictionary);
  const label = dictionary[key].label;
  const displayName = label.toLowerCase() === "unassigned" ? "Unassigned Floors" : label;

  const reportTitle = keys.length === 1 && key === "unassigned" ? reportType : `${reportType} - ${displayName}`;
  const sheetName = await safeWorksheetName(workbook, reportTitle);
  newSheet.name = sheetName;

  // Update the title named range to include report title with key
  const titleRange = newSheet.names.getItem(rangeNames.title).getRange();
  titleRange.values = [[reportTitle]];

  // Set custom properties for new sheet
  newSheet.visibility = Excel.SheetVisibility.visible;
  newSheet.customProperties.add(WORKSHEET_CUSTOM_PROPERTIES.REPORT_TYPE, reportType);
  // NOTE: Will this cause issues with the edge case that we're not handling is line items allocated to multiple use groups with the same label (DO-135)
  newSheet.customProperties.add(WORKSHEET_CUSTOM_PROPERTIES.LABEL, key);
  await context.sync();

  // add date time stamp
  dateStampSheet(newSheet);
  return {
    key,
    label,
    data: dictionary[key].data,
    sheetName: newSheet.name,
  };
};

export const handleDynamicReportGeneration = async (
  context: Excel.RequestContext,
  template: Excel.Worksheet,
  reportType: string,
  data: any[],
  splitByKey: string,
  labelKey?: string
): Promise<SheetDataItem[]> => {
  const reportSheetInfo: SheetDataItem[] = [];
  const dictionary = groupDataByKey(data, splitByKey, labelKey);
  const keys = Object.keys(dictionary);
  const workbook = context.workbook;
  const worksheets = workbook.worksheets;
  worksheets.load("items/name");
  await context.sync();

  for (const worksheet of worksheets.items) {
    const props = await getWorksheetCustomProperties(context, worksheet);
    const key = props.get(WORKSHEET_CUSTOM_PROPERTIES.LABEL) as string;
    if (
      props.get(WORKSHEET_CUSTOM_PROPERTIES.REPORT_TYPE) === reportType &&
      props.get(WORKSHEET_CUSTOM_PROPERTIES.IS_TEMPLATE) !== "true"
    ) {
      // If sheet no longer has data, delete the sheet
      if (!dictionary[key]) {
        worksheet.delete();
        await context.sync();
      } else {
        // If sheet is named "Unassigned", rename it to include/remove " - Unassigned Floors"
        if (key.toLowerCase() === "unassigned") {
          const reportTitle = keys.length === 1 ? reportType : `${reportType} - Unassigned Floors`;
          const sheetName = await safeWorksheetName(workbook, reportTitle, false);
          worksheet.name = sheetName;
          await context.sync();
        }
        await updateReportDate(worksheet);
        // If sheet exists and has data, add to reportSheetInfo
        reportSheetInfo.push({
          key,
          label: dictionary[key].label,
          data: dictionary[key].data,
          sheetName: worksheet.name,
        });
      }
    }
  }

  // If sheet exists in data but not in workbook, create it from template
  for (const key of keys) {
    if (!reportSheetInfo.find((sheet) => sheet.key === key)) {
      const newSheet = await generateDynamicSheetWithTemplate(context, workbook, template, reportType, key, dictionary);
      reportSheetInfo.push(newSheet);
    }
  }

  await context.sync();
  return reportSheetInfo;
};
